/**
 * Audio Notification Service
 * Manages notification sound playback with optional repeat count or infinite loop
 */

class AudioNotificationService {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
    this.soundFile = null;
    this.playLimit = 0;
    this.playsRemaining = 0;
    this.onCompleteCallback = null;
    this.handleEnded = this.handleEnded.bind(this);
  }

  /**
   * Initialize the audio service with a sound file
   * @param {string} soundFile - Path to the sound file
   */
  init(soundFile) {
    if (!this.audio || this.soundFile !== soundFile) {
      if (this.audio) {
        this.audio.removeEventListener("ended", this.handleEnded);
      }

      this.soundFile = soundFile;
      this.audio = new Audio(soundFile);

      this.audio.addEventListener("play", () => {
        this.isPlaying = true;
      });

      this.audio.addEventListener("pause", () => {
        this.isPlaying = false;
      });

      this.audio.addEventListener("ended", this.handleEnded);
    }
  }

  handleEnded() {
    this.isPlaying = false;

    if (this.playLimit === 0) {
      return;
    }

    this.playsRemaining -= 1;

    if (this.playsRemaining > 0) {
      this.audio.currentTime = 0;
      this.audio.play().catch((error) => {
        console.error("Error replaying notification sound:", error);
        this.finishPlayback();
      });
      return;
    }

    this.finishPlayback();
  }

  finishPlayback() {
    const callback = this.onCompleteCallback;
    this.onCompleteCallback = null;
    this.playLimit = 0;
    this.playsRemaining = 0;

    if (callback) {
      callback();
    }
  }

  /**
   * Play the notification sound
   * @param {Object} options
   * @param {number} options.playCount - 0 = loop until stopped, 1+ = play that many times
   * @param {Function} options.onComplete - Called when a finite play sequence finishes
   * @returns {Promise<void>}
   */
  async play({ playCount = 0, onComplete = null } = {}) {
    if (!this.audio) {
      console.warn("Audio not initialized. Call init() first.");
      return;
    }

    this.playLimit = playCount;
    this.playsRemaining = playCount;
    this.onCompleteCallback = onComplete;
    this.audio.loop = playCount === 0;

    try {
      this.audio.currentTime = 0;
      await this.audio.play();
    } catch (error) {
      console.error("Error playing notification sound:", error);
      this.isPlaying = false;
      this.onCompleteCallback = null;
      this.playLimit = 0;
      this.playsRemaining = 0;
    }
  }

  /**
   * Stop the notification sound
   */
  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio.loop = false;
    }

    this.isPlaying = false;
    this.onCompleteCallback = null;
    this.playLimit = 0;
    this.playsRemaining = 0;
  }

  /**
   * Restart the sound (stop and play again)
   * Used when a new notification comes in
   * @param {Object} options
   * @param {number} options.playCount - 0 = loop until stopped, 1+ = play that many times
   * @param {Function} options.onComplete - Called when a finite play sequence finishes
   * @returns {Promise<void>}
   */
  async restart({ playCount = 0, onComplete = null } = {}) {
    this.stop();
    await this.play({ playCount, onComplete });
  }

  /**
   * Check if sound is currently playing
   * @returns {boolean}
   */
  isCurrentlyPlaying() {
    return this.isPlaying;
  }

  /**
   * Set volume (0.0 to 1.0)
   * @param {number} volume - Volume level
   */
  setVolume(volume) {
    if (this.audio) {
      this.audio.volume = Math.max(0, Math.min(1, volume));
    }
  }
}

// Export singleton instance
const audioNotificationService = new AudioNotificationService();

// Initialize with default sound file
try {
  audioNotificationService.init(require("../assets/sounds/online_order_notificaiton.wav"));
} catch (error) {
  console.error("Failed to initialize audio notification service:", error);
}

export default audioNotificationService;
